<template>
  <main-layout v-if="survey">
    <template slot="header-title">{{ survey.name }}</template>
    <template slot="header-actions">
      <common-button type="secondary">
        <router-link class="text-gray-600" :to="{ name: 'Surveys', params: { site: site.sitekey } }">
          Cancel
        </router-link>
      </common-button>
      <common-button @click="save()">Save </common-button>
    </template>

    <template slot="main">
      <common-ui-form-general
        v-if="survey"
        type="survey"
        :code="survey.code"
        :name="survey.name"
        :status="survey.status"
        :activationdate="survey.activationdate"
        :deactivationdate="survey.deactivationdate"
        :isCsv="false"
        :loading="loading"
        @update-name="name => (fields.name = name)"
        @update-status="status => (fields.status = status)"
        @update-activationdate="dateStart => updateDateStart(dateStart)"
        @update-deactivationdate="dateEnd => updateDateEnd(dateEnd)"
      />

      <surveys-ui-form-content
        v-if="survey"
        :question="survey.question"
        @update-question="question => (fields.question = question)"
      />

      <common-ui-form-trigger
        v-if="survey"
        type="Survey"
        :data="survey"
        @update-frequencylimitationvalue="
          newfrequencylimitationvalue =>
            (fields.frequencylimitationvalue = newfrequencylimitationvalue)
        "
        @update-frequencylimitation="
          newFrequencylimitation =>
            (fields.frequencylimitation = newFrequencylimitation)
        "
        @update-displayintervaldelayseen="newIntervaldelayseen => (fields.displayintervaldelayseen = newIntervaldelayseen)"
        @update-displayintervaldelayanswered="newIntervaldelayanswered => (fields.displayintervaldelayanswered = newIntervaldelayanswered)"
      />

      <common-ui-form-targeting
        v-if="survey"
        type="Survey"
        :current-url="survey.url"
        :target-url="survey.url_target"
        :target-url-list="survey.url_target_list"
        :exclude-url="survey.url_exclude_list"
        @update-fields-url="url => (fields.url = url)"
        @update-path-target="urlTarget => (fields.url_target_list = urlTarget)"
        @update-path-exclude="
          urlExclude => (fields.url_exclude_list = urlExclude)
        "
      />

      <common-ui-form-audience
        v-if="survey.target_audience && survey.bubbles"
        :key="audienceKey"
        type="survey"
        :isCsv="false"
        :target-audience="survey.target_audience"
        :excluded-audience="survey.excluded_audience"
        :bubbles="survey.bubbles"
        :excluded-bubbles="survey.excluded_bubbles"
        @update-audience="
          audienceData => (fields.target_audience = audienceData)
        "
        @update-excluded-audience="
          audienceData => (fields.excluded_audience = audienceData)
        "
        @update-bubbles="bubble => updateFieldsBubble(bubble)"
        @update-excluded-bubbles="bubble => updateFieldsExcludedBubble(bubble)"
        @erase-bubble="bubbles => (fields.bubbles = bubbles)"
        @erase-excluded-bubble="bubbles => (fields.excluded_bubbles = bubbles)"
      />

      <template>
        <div style="position: fixed; bottom: 20px; left: 20px; margin: 0; z-index: 9999;">
          <common-ui-alert
            type="warning"
            message="You have modification not saved. Don't forget to save it."
            :base="survey"
            :object="fields"
          />
        </div>
      </template>

      <modal-confirmation
        v-if="modals.isOpenDeletionConfirmation"
        :open="modals.isOpenDeletionConfirmation"
        type="danger"
        messageTitle="Warnings !"
        message="Do you really want to remove this survey ? This actions cannot be undone."
        messageAction="Yes, I'm sure !"
        @execute="handleConfirmDeletion"
        @close="modals.isOpenDeletionConfirmation = false"
      />
    </template>
  </main-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Survey',
  components: {},
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      fields: {
        id: null,
        code: null,
        name: null,
        url: null,
        activationdate: null,
        deactivationdate: null,
        displayintervaldelayseen: null,
        displayintervaldelayanswered: null,
        target_audience: null,
        excluded_audience: null,
        status: null,
        bubbles: [],
        excluded_bubbles: [],
        url_target_list: [],
        url_exclude_list: [],
        question: null,
        range: [],
      },
      loading: false,
      audienceKey: 0,
      modals: {
        isOpenDeletionConfirmation: false
      },
      fieldsError: {
        name: null,
        url: null,
        question: null
      }
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),

    ...mapGetters('surveys', {
      getById: 'getById'
    }),

    languages () {
      return this.site.languages.map(lang => lang.lang)
    },

    survey() {
      const survey = this.getById(this.id)
      return survey || null
    }
  },
  watch: {
    site: {
      handler: 'initSurvey',
      immediate: true
    }
  },
  methods: {
    ...mapActions('surveys', ['loadSurvey', 'updateSurvey', 'removeSurvey']),

    initSurvey() {
      if (!this.site) return

      this.loadSurvey({
        id: this.id
      }).then((survey) => {
        this.updateFields(survey)
      })
    },

    checkInputs() {
      const ValidHostnameRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/

      let error = false

      if (!this.fields.name) {
        this.$notify({
          title: 'Error',
          message: 'Name must be a valid string, like: Welcome dear !',
          type: 'error'
        })
        error = true
      }

      if (
        !this.fields.url ||
        !Array.isArray(this.fields.url.match(ValidHostnameRegex)) ||
        (Array.isArray(this.fields.url.match(ValidHostnameRegex)) &&
          !this.fields.url.match(ValidHostnameRegex).length)
      ) {
        this.$notify({
          title: 'Error',
          message: 'Hostname must be a valid string, like: rocketfid.com',
          type: 'error'
        })
        error = true
      }

      for (let lang of this.languages) {
        if (!this.fields.question || !this.fields.question[lang] || this.fields.question[lang] === '') {
          this.$notify({
          title: 'Error',
          message: 'Question must be a valid string, like: Welcome dear !',
          type: 'error'
        })
          error = true
        }
      }

      return !error
    },

    updateFields(survey) {
      this.fields = Object.assign({}, survey);
    },

    async save() {
      this.fieldsError.name = null
      this.fieldsError.url = null
      this.fieldsError.question = null

      if (this.fields.target_audience === 'all') {
        this.fields.bubbles = []
      }

      if (this.fields.excluded_audience === 'no-one') {
        this.fields.excluded_bubbles = []
      }

      if (!this.checkInputs()) {
        return false
      }

      try {
        const newSurvey = await this.updateSurvey(this.fields)
        this.$notify({
          title: 'Success',
          message: 'Survey has been updated',
          type: 'success'
        })
        this.updateFields(newSurvey)
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'An error occured',
          type: 'error'
        })
      }
    },

    updateDateStart(ds) {
      this.fields.activationdate = ds
    },

    updateDateEnd(de) {
      this.fields.deactivationdate = de
    },

    updateDelaySeen(dse) {
      this.fields.displayintervaldelayseen = dse
    },

    updateDelayAnswered(daw) {
      this.fields.displayintervaldelayanswered = daw
    },

    handleConfirmDeletion() {
      this.removeSurvey({
        id: this.id
      })
    },

    updateFieldsBubble(bubble) {
      const indexBubble = this.fields.bubbles.findIndex(b => b.id === bubble.id)
      const bubbleExist = indexBubble !== -1
      if (bubbleExist) {
        this.fields.bubbles.splice(indexBubble, 1)
      }

      this.fields.bubbles.push(bubble)
    },

    updateFieldsExcludedBubble(bubble) {
      const indexBubble = this.fields.excluded_bubbles.findIndex(
        b => b.id === bubble.id
      )
      const bubbleExist = indexBubble !== -1
      if (bubbleExist) {
        this.fields.excluded_bubbles.splice(indexBubble, 1)
      }

      this.fields.excluded_bubbles.push(bubble)
    },
  }
}
</script>


// WEBPACK FOOTER //
// src/pages/Survey/Survey.vue